import { formatInTimeZone } from 'date-fns-tz';

/**
 * Independence Day (15 Aug) festive layer.
 *
 * The festive window runs 14–16 August (IST), inclusive — so the treatment
 * shows on the eve, the day, and the day after, then disappears on its own.
 * All checks are IST-based so the "day" is consistent regardless of the
 * viewer's local timezone.
 */
const TZ = 'Asia/Kolkata';
const WINDOW = new Set(['08-14', '08-15', '08-16']);

/**
 * Dev/preview override so the festive layer can be seen outside the window.
 * Visit any page with `?iday=1` to force it on (sticks via localStorage),
 * or `?iday=0` to turn it back off. Remove before shipping if desired.
 */
function previewForced(): boolean {
  try {
    const p = new URLSearchParams(window.location.search).get('iday');
    if (p === '1') { localStorage.setItem('andhub_iday_preview', '1'); return true; }
    if (p === '0') { localStorage.removeItem('andhub_iday_preview'); return false; }
    return localStorage.getItem('andhub_iday_preview') === '1';
  } catch { return false; }
}

export function isIndependenceWindow(now: Date = new Date()): boolean {
  // Strictly the 14–16 August (IST) window — no force-on override in production,
  // so the festive layer only ever appears on those three days.
  return WINDOW.has(formatInTimeZone(now, TZ, 'MM-dd'));
}

/** True when the festive layer is being force-previewed (?iday=1). While
 *  previewing, callers ignore the per-year "dismissed" flag so it always shows. */
export function isIndependencePreview(): boolean {
  return previewForced();
}

/** Tricolor gradient for the app-wide top strip and accents. */
export const TRICOLOR_GRADIENT =
  'linear-gradient(90deg, #FF9933 0%, #FF9933 33.33%, #ffffff 33.33%, #ffffff 66.66%, #138808 66.66%, #138808 100%)';

export const TRICOLOR = { saffron: '#FF9933', white: '#ffffff', green: '#138808', chakra: '#0A3299' };

/** Independence Day inspirational / motivational quotes ({ text, author }). */
export const INDEPENDENCE_QUOTES: { text: string; author: string }[] = [
  { text: 'Freedom is not worth having if it does not include the freedom to make mistakes.', author: 'Mahatma Gandhi' },
  { text: 'At the stroke of the midnight hour, when the world sleeps, India will awake to life and freedom.', author: 'Jawaharlal Nehru' },
  { text: 'Arise, awake, and stop not till the goal is reached.', author: 'Swami Vivekananda' },
  { text: 'The future depends on what we do in the present.', author: 'Mahatma Gandhi' },
  { text: 'Where the mind is without fear and the head is held high — into that heaven of freedom, let my country awake.', author: 'Rabindranath Tagore' },
  { text: 'Every citizen of India must remember that he is an Indian and he has every right in this country but with certain duties.', author: 'Sardar Vallabhbhai Patel' },
  { text: 'Dream is not that which you see while sleeping; it is something that does not let you sleep.', author: 'A. P. J. Abdul Kalam' },
  { text: 'The best way to find yourself is to lose yourself in the service of others.', author: 'Mahatma Gandhi' },
];
